import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Date-range filter — a dependency-free React port of the legacy jQuery
 * `daterangepicker` (dangrossman) used in `.old` on the metrics console.
 *
 * Single read-only "Filter By Date Range" field that opens a dual-month
 * calendar popup with range highlighting and Clear / Apply actions, matching
 * the legacy look (ADS green selection + Apply button).
 *
 * Values are exchanged as ISO `yyyy-mm-dd` strings (or '' when unset).
 */

type Props = {
  from: string;
  to: string;
  onApply: (from: string, to: string) => void;
  onClear: () => void;
  placeholder?: string;
};

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const pad = (n: number) => String(n).padStart(2, '0');
const toISO = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fmt = (d: Date) => `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}`;
const parseISO = (s: string) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};
const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonths = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth() + n, 1);
const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const stripTime = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

function monthCells(month: Date): Date[] {
  const first = startOfMonth(month);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay()); // back up to the Sunday on/before the 1st
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

export function DateRangePicker({ from, to, onApply, onClear, placeholder = 'Filter By Date Range' }: Props) {
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState<Date | null>(from ? parseISO(from) : null);
  const [end, setEnd] = useState<Date | null>(to ? parseISO(to) : null);
  const [leftMonth, setLeftMonth] = useState<Date>(startOfMonth(from ? parseISO(from) : new Date()));
  const wrapRef = useRef<HTMLDivElement>(null);

  // Keep internal selection in sync when the parent value changes externally.
  useEffect(() => {
    setStart(from ? parseISO(from) : null);
    setEnd(to ? parseISO(to) : null);
  }, [from, to]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const display = from && to ? `${fmt(parseISO(from))} - ${fmt(parseISO(to))}` : '';

  const openPicker = () => {
    setStart(from ? parseISO(from) : null);
    setEnd(to ? parseISO(to) : null);
    setLeftMonth(startOfMonth(from ? parseISO(from) : new Date()));
    setOpen(true);
  };

  const pickDay = (day: Date) => {
    const d = stripTime(day);
    if (!start || end) {
      setStart(d);
      setEnd(null);
    } else if (d < start) {
      setEnd(start);
      setStart(d);
    } else {
      setEnd(d);
    }
  };

  const apply = () => {
    if (start) {
      const s = start;
      const e = end ?? start;
      onApply(toISO(s), toISO(e));
    }
    setOpen(false);
  };

  const clear = () => {
    setStart(null);
    setEnd(null);
    onClear();
    setOpen(false);
  };

  const footerText = start
    ? `${fmt(start)} - ${fmt(end ?? start)}`
    : 'Select a start and end date';

  return (
    <div className="drp-wrap h-100" ref={wrapRef}>
      <input
        type="text"
        readOnly
        value={display}
        placeholder={placeholder}
        onClick={() => (open ? setOpen(false) : openPicker())}
        className="form-control h-100"
        style={{ cursor: 'pointer', background: '#fff' }}
      />

      {open && (
        <div className="drp-popup">
          <div className="d-flex flex-wrap">
            <MonthTable
              month={leftMonth}
              side="left"
              start={start}
              end={end}
              onPrev={() => setLeftMonth((m) => addMonths(m, -1))}
              onNext={() => setLeftMonth((m) => addMonths(m, 1))}
              onPick={pickDay}
            />
            <MonthTable
              month={addMonths(leftMonth, 1)}
              side="right"
              start={start}
              end={end}
              onPrev={() => setLeftMonth((m) => addMonths(m, -1))}
              onNext={() => setLeftMonth((m) => addMonths(m, 1))}
              onPick={pickDay}
            />
          </div>
          <div className="drp-footer d-flex align-items-center">
            <span className="me-auto small text-muted">{footerText}</span>
            <button type="button" className="btn btn-link link-dark p-0 me-3" onClick={clear}>
              Clear
            </button>
            <button type="button" className="btn btn-primary btn-sm px-3" onClick={apply} disabled={!start}>
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MonthTable({
  month, side, start, end, onPrev, onNext, onPick,
}: {
  month: Date;
  side: 'left' | 'right';
  start: Date | null;
  end: Date | null;
  onPrev: () => void;
  onNext: () => void;
  onPick: (d: Date) => void;
}) {
  const cells = useMemo(() => monthCells(month), [month]);
  const rangeEnd = end ?? start;

  return (
    <table className="drp-cal">
      <thead>
        <tr>
          <th className="drp-arrow">
            {side === 'left' ? (
              <button type="button" className="btn btn-sm p-0 border-0" onClick={onPrev} aria-label="Previous month">
                &lsaquo;
              </button>
            ) : null}
          </th>
          <th colSpan={5} className="drp-title">
            {MONTHS[month.getMonth()]} {month.getFullYear()}
          </th>
          <th className="drp-arrow">
            {side === 'right' ? (
              <button type="button" className="btn btn-sm p-0 border-0" onClick={onNext} aria-label="Next month">
                &rsaquo;
              </button>
            ) : null}
          </th>
        </tr>
        <tr>
          {WEEKDAYS.map((w) => (
            <th key={w} className="drp-wd">{w}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: 6 }, (_, row) => (
          <tr key={row}>
            {cells.slice(row * 7, row * 7 + 7).map((d) => {
              const inMonth = d.getMonth() === month.getMonth();
              const isStart = start ? sameDay(d, start) : false;
              const isEnd = rangeEnd ? sameDay(d, rangeEnd) : false;
              const inRange =
                start && rangeEnd ? stripTime(d) >= stripTime(start) && stripTime(d) <= stripTime(rangeEnd) : false;
              const cls = [
                'drp-day',
                inMonth ? '' : 'drp-muted',
                inRange ? 'drp-in-range' : '',
                isStart || isEnd ? 'drp-selected' : '',
              ].filter(Boolean).join(' ');
              return (
                <td key={toISO(d)} className={cls} onClick={() => onPick(d)}>
                  {d.getDate()}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
